/**
 * Layout shell for the redesigned Manage Returns area: loads portal/UI settings and renders child routes via Outlet.
 * Leaves the redesign when the current shop lacks the feature or the user opted out.
 */
import { observer } from "mobx-react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import BasePageLayout from "../../components/layouts/base-page-layout/base-page-layout";
import { useEffect } from "react";
import currentPortalStore from "../../stores/current-portal-store";
import dashboardSettingsStore from "../../stores/dashboard-settings-store";
import uiSettingsStore from "../../stores/ui-settings-store";
import useDashboardTranslation from "../../dashboard-translation/hooks/dashboard-translation-hook";
import { APP_PATHS } from "../../constants/app-paths";
import ManageReturnsRedesignToggle from "./components/manage-returns-redesign-toggle/manage-returns-redesign-toggle";
import {
    buildManageReturnsDesignPath,
    getPreferredManageReturnsListPath,
    isManageReturnsDesignToggleVisible,
    shouldUseNewManageReturnsDesign,
} from "./utils/manage-returns-redesign-rollout";
/** Side effect: register redesign list for RMA prev/next (also when landing on :rmaId without list). */
import "./rma-list-page-new/stores/rma-tabs-paged-list-store";

const ManageReturnsPageNew: React.FC = observer(() => {
    const { getTranslation } = useDashboardTranslation();
    const { rmaId } = useParams();
    const isRmaListPage = !rmaId;
    const navigate = useNavigate();

    useEffect(() => {
        currentPortalStore.load();
        uiSettingsStore.load();
    }, []);

    // Leave redesign when the shop feature is off or the user opted out (no full reload required).
    useEffect(() => {
        if (
            !uiSettingsStore.isLoaded ||
            !dashboardSettingsStore.areSettingsLoaded ||
            shouldUseNewManageReturnsDesign()
        ) {
            return;
        }
        navigate(buildManageReturnsDesignPath(false, rmaId), { replace: true });
    }, [
        uiSettingsStore.isLoaded,
        uiSettingsStore.manageReturnsNewEnabled,
        dashboardSettingsStore.areSettingsLoaded,
        dashboardSettingsStore.manageReturnsRedesignAvailable,
        rmaId,
        navigate,
    ]);

    const onBackButtonClick = () => {
        navigate(getPreferredManageReturnsListPath() ?? APP_PATHS.RETURNS);
    };

    return (
        <BasePageLayout
            title={getTranslation("MANAGE_RETURNS", "dashboard-returns-page")}
            backButtonCallback={onBackButtonClick}
            titleActions={isRmaListPage && isManageReturnsDesignToggleVisible() ? <ManageReturnsRedesignToggle /> : undefined}
        >
            <Outlet />
        </BasePageLayout>
    );
});

export default ManageReturnsPageNew;
